import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import { I18nProvider } from "./lib/i18n";
import { RealtimeProvider } from "./lib/realtime";
import { registerWebPush } from "./lib/webpush";
import { DialogProvider } from "./components/Dialog";
import NotificationToast from "./components/NotificationToast";
import ChecklistDueAlert from "./components/ChecklistDueAlert";
import Login from "./pages/Login";
import Home from "./pages/Home";
import Complaints from "./pages/Complaints";
import ComplaintDetail from "./pages/ComplaintDetail";
import Chat from "./pages/Chat";
import Account from "./pages/Account";
import Notifications from "./pages/Notifications";
import DailyChecklist from "./pages/DailyChecklist";
import Tasks from "./pages/Tasks";
import DailyReport from "./pages/DailyReport";
import FlatDirectory from "./pages/FlatDirectory";
import Tenants from "./pages/Tenants";
import Oversight from "./pages/Oversight";
import Admin from "./pages/Admin";
import FeatureModules from "./pages/FeatureModules";
import ManagerHome from "./pages/ManagerHome";
import SecurityHome from "./pages/SecurityHome";
import Visitors from "./pages/Visitors";
import VisitorApprove from "./pages/VisitorApprove";
import CashBook from "./pages/CashBook";
import Bookings from "./pages/Bookings";
import Polls from "./pages/Polls";
import Sos from "./pages/Sos";
import Documents from "./pages/Documents";
import SocietyBills from "./pages/SocietyBills";
import VisitorApprovalListener from "./components/VisitorApprovalListener";
import StaffAttendance from "./pages/StaffAttendance";
import AttendanceSummary from "./pages/AttendanceSummary";
import RosterMaster from "./pages/RosterMaster";
import ChecklistMaster from "./pages/ChecklistMaster";
import Announcements from "./pages/Announcements";
import ParkingLookup from "./pages/ParkingLookup";
import AppShell from "./components/AppShell";

function Guard({ children, roles, feature }) {
  const { user, booting, isFeatureEnabled } = useAuth();
  const loc = useLocation();
  if (booting) return <div className="app-shell bg-aurora flex items-center justify-center"><div className="text-white/60 text-sm">Loading…</div></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Defense in depth: a disabled module's route redirects home (the API also 403s).
  if (feature && !isFeatureEnabled(feature)) return <Navigate to="/" replace />;
  return children;
}

function Router() {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const isStaff = user && ["MANAGER", "CHAIRPERSON", "ADMIN"].includes(user.role);
  const isSecurity = user && user.role === "SECURITY";
  const STAFF = ["MANAGER", "CHAIRPERSON", "ADMIN"];
  const OPS = ["MANAGER", "CHAIRPERSON", "ADMIN", "SECURITY"];

  // Restore the last screen after a quick WebView reload (e.g. returning from the
  // native camera), so the user stays where they were instead of dropping to home.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("sp.route");
      if (raw && loc.pathname === "/") {
        const { path, t } = JSON.parse(raw);
        if (path && path !== "/" && Date.now() - t < 90000) navigate(path, { replace: true });
      }
    } catch {}
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    try { localStorage.setItem("sp.route", JSON.stringify({ path: loc.pathname + loc.search, t: Date.now() })); } catch {}
  }, [loc]);

  // Register for web/PWA push once logged in (browser notification + sound).
  useEffect(() => { if (user) registerWebPush(); }, [user]);

  return (
    <>
      {user && <NotificationToast />}
      {user && <ChecklistDueAlert />}
      {user && <VisitorApprovalListener />}
      <Routes>
        <Route path="/parking" element={<ParkingLookup />} />
        <Route path="/society-bills" element={<SocietyBills />} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />

        <Route element={<Guard><AppShell /></Guard>}>
          <Route path="/" element={isSecurity ? <SecurityHome /> : isStaff ? <ManagerHome /> : <Home />} />
          <Route path="/complaints" element={<Guard feature="complaints"><Complaints /></Guard>} />
          <Route path="/complaints/:id" element={<Guard feature="complaints"><ComplaintDetail /></Guard>} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/checklist" element={<Guard feature="checklist"><DailyChecklist /></Guard>} />
          <Route path="/chat" element={<Guard feature="chat"><Chat /></Guard>} />
          <Route path="/account" element={<Account />} />
          <Route path="/visitors" element={<Guard feature="visitors"><Visitors /></Guard>} />
          <Route path="/visitors/approve/:id" element={<Guard feature="visitors"><VisitorApprove /></Guard>} />
          <Route path="/bookings" element={<Guard feature="bookings"><Bookings /></Guard>} />
          <Route path="/polls" element={<Guard feature="polls"><Polls /></Guard>} />
          <Route path="/sos" element={<Guard feature="sos"><Sos /></Guard>} />
          <Route path="/documents" element={<Guard feature="documents"><Documents /></Guard>} />

          <Route path="/tasks" element={<Guard roles={STAFF} feature="complaints"><Tasks /></Guard>} />
          <Route path="/daily-report" element={<Guard roles={STAFF}><DailyReport /></Guard>} />
          <Route path="/flats" element={<Guard roles={OPS} feature="flats"><FlatDirectory /></Guard>} />
          <Route path="/tenants" element={<Guard roles={OPS} feature="tenants"><Tenants /></Guard>} />
          <Route path="/oversight" element={<Guard roles={["CHAIRPERSON", "ADMIN"]} feature="oversight"><Oversight /></Guard>} />
          <Route path="/admin" element={<Guard roles={["ADMIN"]}><Admin /></Guard>} />
          <Route path="/admin/features" element={<Guard roles={["ADMIN"]}><FeatureModules /></Guard>} />
          <Route path="/staff" element={<Guard feature="attendance"><StaffAttendance /></Guard>} />
          <Route path="/staff/:category" element={<Guard feature="attendance"><StaffAttendance /></Guard>} />
          <Route path="/attendance-summary" element={<Guard roles={STAFF} feature="attendance"><AttendanceSummary /></Guard>} />
          <Route path="/cashbook" element={<Guard roles={STAFF} feature="cashbook"><CashBook /></Guard>} />
          <Route path="/masters/staff/:category" element={<Guard roles={OPS} feature="attendance"><RosterMaster /></Guard>} />
          <Route path="/masters/checklist" element={<Guard roles={OPS} feature="checklist"><ChecklistMaster /></Guard>} />
          <Route path="/announcements" element={<Guard roles={STAFF} feature="announcements"><Announcements /></Guard>} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <AuthProvider>
          <RealtimeProvider>
            <DialogProvider>
              <BrowserRouter>
                <Router />
              </BrowserRouter>
            </DialogProvider>
          </RealtimeProvider>
        </AuthProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
